import { useEffect, useRef, useState } from "react";
import icon1 from "../../Asset/Install Zero Typing.svg";
import icon2 from "../../Asset/Complete our guided setup wizard.svg";
import icon3 from "../../Asset/Open any app with a text field.svg";
import icon4 from "../../Asset/Place your cursor & Start speaking with Zero Typing app.svg";
import icon5 from "../../Asset/Watch your words appear instantly.svg";
import { ReactSVG } from "react-svg";
import { useInView } from "react-intersection-observer";
import { useBreakpoint } from "./Home";

const HowItWorkVideo = () => {
  const barLabels = [
    "Install Zero Typing",
    "Complete our guided setup wizard",
    "Open any app with a text field",
    "Place your cursor & Start speaking with Zero Typing app",
    // "Watch your words appear instantly",
  ];

  // When the whole block is on-screen (controls play/pause + resume)
  const { ref, inView } = useInView({ triggerOnce: false, threshold: 0.5 });

  // For slide-in animations only
  const { ref: videoRef, inView: videoView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const isMobile = useBreakpoint();
  const { ref: barRef, inView: barView } = useInView({
    triggerOnce: true,
    threshold: isMobile ? 0.1 : 0.5,
  });

  const labelsIcon = [icon1, icon2, icon3, icon4];

  const sources = [
    {
      type: "video",
      url: "https://pub-25f4599302a64a3dab9e149b2b5f9a0f.r2.dev/videos/INSTALL.mp4",
    },
    {
      type: "video",
      url: "https://pub-25f4599302a64a3dab9e149b2b5f9a0f.r2.dev/videos/Setup%20WIzard.mp4",
    },
    {
      type: "video",
      url: "https://pub-25f4599302a64a3dab9e149b2b5f9a0f.r2.dev/videos/Open%20ANy%20App.mp4",
    },
    {
      type: "video",
      // url: "https://pub-25f4599302a64a3dab9e149b2b5f9a0f.r2.dev/videos/Start%20Recording.mp4",
      url: "https://res.cloudinary.com/dc3pemeon/video/upload/v1782716964/1TAP_AND_RECORD_e5rwdn.mp4"
    }

  ];

  const [progress, setProgress] = useState(Array(sources.length).fill(0));
  const [current, setCurrent] = useState(0);
  const [activeData, setActiveData] = useState(sources[0]);
  const [fade, setFade] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [prevVideo, setPrevVideo] = useState(null);

  // Refs
  const activeVideoRef = useRef(null);
  const prevVideoRef = useRef(null);
  const timeByIndexRef = useRef(sources.map(() => 0)); // per-step currentTime
  const durationByIndexRef = useRef(sources.map(() => 0)); // per-step duration

  // Helpers
  const setCurrentTimeSafely = (el, t) => {
    if (!el) return;
    const apply = () => {
      try {
        const dur = Number.isFinite(el.duration) ? el.duration : t || 0;
        const target = Math.max(0, Math.min(t || 0, dur || 0));
        if (!Number.isNaN(target)) el.currentTime = target;
      } catch { }
    };
    if (el.readyState >= 1) apply();
    else {
      const onMeta = () => {
        apply();
        el.removeEventListener("loadedmetadata", onMeta);
      };
      el.addEventListener("loadedmetadata", onMeta);
    }
  };

  const gotoStep = (index) => {
    setFade(false);
    setTimeout(() => {
      setPrevVideo(activeData);
      setCurrent(index);
      setActiveData(sources[index]);
      setIsVideoReady(false);
      setFade(true);
    }, 400);
  };

  const gotoNext = () => {
    const nextIndex = (current + 1) % sources.length;
    gotoStep(nextIndex);
  };

  // Pause/play + restore timestamp on visibility change
  useEffect(() => {
    const v = activeVideoRef.current;
    const p = prevVideoRef.current;
    const shouldPlay = inView && isVideoReady;

    const pauseAndSave = (el) => {
      if (!el) return;
      try {
        el.pause?.();
        if (el === v) timeByIndexRef.current[current] = el.currentTime || 0;
      } catch { }
    };
    const restoreAndPlay = (el, index) => {
      if (!el) return;
      setCurrentTimeSafely(el, timeByIndexRef.current[index] || 0);
      const prom = el.play?.();
      if (prom && typeof prom.catch === "function") prom.catch(() => { });
    };

    if (shouldPlay) {
      restoreAndPlay(v, current);
      p?.play?.();
    } else {
      pauseAndSave(v);
      p?.pause?.();
    }
  }, [inView, isVideoReady, activeData, current]);

  // Pause when tab hidden, resume when visible & in view
  useEffect(() => {
    const onVis = () => {
      const hidden = document.visibilityState !== "visible";
      const v = activeVideoRef.current;
      const p = prevVideoRef.current;
      if (!v && !p) return;

      if (hidden) {
        try {
          v?.pause?.();
          timeByIndexRef.current[current] = v?.currentTime || 0;
          p?.pause?.();
        } catch { }
      } else if (inView && isVideoReady) {
        setCurrentTimeSafely(v, timeByIndexRef.current[current] || 0);
        const prom = v?.play?.();
        if (prom && typeof prom.catch === "function") prom.catch(() => { });
        p?.play?.();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [inView, isVideoReady, current]);

  // Click: jump to a step (resume from saved time for that step)
  const handleClick = (index) => {
    // Save current time for current step before switching
    const v = activeVideoRef.current;
    if (v) timeByIndexRef.current[current] = v.currentTime || 0;

    // keep whatever progress we had for current; reset the clicked one to saved ratio (or 0)
    setProgress((prev) => {
      const copy = [...prev];
      const dur = durationByIndexRef.current[index] || 0;
      const t = timeByIndexRef.current[index] || 0;
      copy[index] = dur > 0 ? Math.min(100, (t / dur) * 100) : 0;
      return copy;
    });

    gotoStep(index);
  };

  // Attach listeners to the active video to drive progress = (currentTime/duration)*100 and auto-advance
  useEffect(() => {
    const el = activeVideoRef.current;
    if (!el) return;

    const onLoadedMetadata = () => {
      durationByIndexRef.current[current] = Number.isFinite(el.duration)
        ? el.duration
        : 0;
      // ensure we resume exactly
      setCurrentTimeSafely(el, timeByIndexRef.current[current] || 0);
    };

    const onTimeUpdate = () => {
      const dur = durationByIndexRef.current[current] || el.duration || 0;
      const t = el.currentTime || 0;
      timeByIndexRef.current[current] = t;

      const pct = dur > 0 ? Math.max(0, Math.min(100, (t / dur) * 100)) : 0;
      setProgress((prev) => {
        const copy = [...prev];
        copy[current] = pct;
        return copy;
      });
    };

    const onEnded = () => {
      setProgress((prev) => {
        const copy = [...prev];
        copy[current] = 100;
        return copy;
      });
      // cross-fade to next step
      gotoNext();
    };

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);

    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, activeData?.url]);

  // Renderer
  const renderVideo = (data, isDesktop = false) => (
    <div
      className={`relative w-full rounded-3xl border ${isDesktop ? "border-transparent bg-[#D7EFFC]" : "border-[#3AB1F6]"
        } overflow-hidden transition-opacity duration-500 ${fade ? "opacity-100" : "opacity-0"
        }`}
    >
      {/* Previous video fading out */}
      {prevVideo && (
        <video
          key={prevVideo?.url + "_prev"}
          ref={prevVideoRef}
          className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500"
          src={prevVideo?.url}
          autoPlay
          loop
          muted
          playsInline
        />
      )}

      {/* Loading state */}
      {/* {!isVideoReady && (
        <div className="absolute inset-0 aspect-square w-full flex items-center justify-center transition-opacity duration-300 opacity-100 bg-[#D7EFFC]">
          <div className="loader"></div>
        </div>
      )} */}

      {/* New video (no loop so we can catch 'ended') */}
      {data?.type === "video" && (
        <video
          key={data?.url}
          ref={activeVideoRef}
          className={`w-full h-full object-cover transition-opacity duration-700 ${isVideoReady ? "opacity-100" : "opacity-0"
            }`}
          src={data?.url}
          autoPlay
          /* loop={false} default is false */
          muted
          playsInline
          preload="auto"
          onCanPlayThrough={() => setIsVideoReady(true)}
        />
      )}
    </div>
  );

  return (
    <div ref={ref} className="w-full flex items-center z-[5] bg-white mt-5">
      {/* Left Side: Steps */}
      <div className="flex-1">
        {progress.map((value, idx) => (
          <div
            ref={barRef}
            key={idx}
            className={`flex flex-col gap-4 cursor-pointer ${barView ? `animate-slideRight anim-preserve` : "opacity-0"
              }`}
            onClick={() => handleClick(idx)}
          >
            <div>
              <span
                className={`flex items-center gap-4 xl:text-[1.375rem] text-[1.1rem] font-inter xl:py-6 py-5 ${idx === current ? "opacity-100 font-semibold" : "opacity-50"
                  }`}
              >
                <ReactSVG
                  src={labelsIcon[idx]}
                  beforeInjection={(svg) => {
                    svg.setAttribute("class", "xl:w-6 w-5 h-auto");
                  }}
                />
                {barLabels[idx]}
              </span>

              {/* Progress Bar */}
              <div className="w-full bg-[#D9D9D9] rounded-full h-[1px]">
                <div
                  className={`h-[1px] rounded-full transition-all duration-100 ${idx === current ? "bg-[#3AB1F6]" : "bg-[#D9D9D9]"
                    }`}
                  style={{ width: `${value}%` }}
                />
              </div>
            </div>

            {/* Mobile View Video */}
            {idx === current && (
              <div className="flex md:hidden justify-center items-center relative h-max">
                {renderVideo(activeData)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Right Side: Desktop Video */}
      <div className="flex-1 md:flex hidden justify-end items-center relative xl:h-[70vh] lg:h-[50vh] h-[40vh]">
        <div
          ref={videoRef}
          className={`${videoView ? `animate-slideLeft anim-preserve` : "opacity-0"
            } w-[85%] transition-opacity duration-500`}
        >
          {renderVideo(activeData, true)}
        </div>
      </div>
    </div>
  );
};

export default HowItWorkVideo;

// import { useEffect, useState } from "react";
// import icon1 from "../../Asset/Install Zero Typing.svg";
// import icon2 from "../../Asset/Complete our guided setup wizard.svg";
// import icon3 from "../../Asset/Open any app with a text field.svg";
// import icon4 from "../../Asset/Place your cursor & Start speaking with Zero Typing app.svg";
// import icon5 from "../../Asset/Watch your words appear instantly.svg";
// import { ReactSVG } from "react-svg";
// import { useInView } from "react-intersection-observer";
// import installApp from "../../Asset/json/install app.json";
// import SetupWizard from "../../Asset/json/Setup Wizard.json";
// import OpenAnyApp from "../../Asset/json/Open any app.json";
// import textAppear from "../../Asset/json/Text Appear.json";
// import Lottie from "lottie-react";
// import { useBreakpoint } from "./Home";

// const HowItWorkVideo = () => {
//   const durations = [4, 4, 4, 10, 3];
//   const barLabels = [
//     "Install Zero Typing",
//     "Complete our guided setup wizard",
//     "Open any app with a text field",
//     "Place your cursor & Start speaking with Zero Typing app",
//     "Watch your words appear instantly",
//   ];
//   const { ref: videoRef, inView: videoView } = useInView({
//     triggerOnce: true,
//     threshold: 0.5,
//   });
//   const isMobile = useBreakpoint();

//   const { ref: barRef, inView: barView } = useInView({
//     triggerOnce: true,
//     threshold: isMobile ? 0.1 : 0.5,
//   });

//   const labelsIcon = [icon1, icon2, icon3, icon4, icon5];
//   const [howItWorkAnimations, setHowItWorkAnimations] = useState([]);

//   const howItWorkUrl = [
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270378/INSTALL.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270405/Setup_WIzard.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270366/Open_ANy_App.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1756383603/Start_Recording.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270381/Text_Appear.mp4",
//     },
//   ];

//   useEffect(() => {
//     const updated = howItWorkUrl.map((item) => {
//       if (item.type === "json") {
//         return {
//           ...item.json,
//           assets: item.json.assets.map((asset) =>
//             asset.u ? { ...asset, u: item.url } : asset
//           ),
//           type: "json",
//         };
//       }

//       if (item.type === "video") {
//         return {
//           url: item.url,
//           type: "video",
//         };
//       }

//       return item;
//     });

//     setHowItWorkAnimations(updated);
//   }, []);

//   const [progress, setProgress] = useState(Array(durations.length).fill(0));
//   const [current, setCurrent] = useState(0);
//   const [activeData, setActiveData] = useState(howItWorkAnimations[0]);
//   const [fade, setFade] = useState(true);

//   const { ref, inView } = useInView({
//     triggerOnce: true,
//     threshold: 0.5,
//   });

//   useEffect(() => {
//     if (!inView || durations.length === 0) return;

//     setActiveData(howItWorkAnimations[current]);

//     const duration = durations[current];
//     const step = 100 / (duration * 10);

//     const interval = setInterval(() => {
//       setProgress((prev) => {
//         const updated = [...prev];
//         updated[current] = Math.min(updated[current] + step, 100);
//         return updated;
//       });
//     }, 100);

//     const timeout = setTimeout(() => {
//       clearInterval(interval);

//       // ensure bar is fully filled
//       setProgress((prev) => {
//         const updated = [...prev];
//         updated[current] = 100;
//         return updated;
//       });

//       // fade and reset before moving to next
//       setFade(false);
//       setTimeout(() => {
//         const nextIndex = (current + 1) % durations.length;

//         // reset all except the next one
//         const resetProgress = Array(durations.length).fill(0);
//         resetProgress[nextIndex] = 0;

//         setProgress(resetProgress);
//         setCurrent(nextIndex);
//         setActiveData(howItWorkAnimations[nextIndex]);
//         setFade(true);
//       }, 400);
//     }, duration * 1000);

//     return () => {
//       clearInterval(interval);
//       clearTimeout(timeout);
//     };
//   }, [current, inView]);

//   const handleClick = (index) => {
//     const resetProgress = Array(durations.length).fill(0);
//     resetProgress[index] = 0;

//     setFade(false);
//     setTimeout(() => {
//       setProgress(resetProgress);
//       setCurrent(index);
//       setActiveData(howItWorkAnimations[index]);
//       setFade(true);
//     }, 400);
//   };

//   return (
//     <div ref={ref} className="w-full flex items-center z-[5] bg-white mt-5">
//       <div className="flex-1">
//         {progress.map((value, idx) => (
//           <div
//             ref={barRef}
//             key={idx}
//             className={`flex flex-col gap-4 cursor-pointer ${
//               barView ? `animate-slideRight` : "opacity-0"
//             }`}
//             onClick={() => handleClick(idx)}
//           >
//             <div>
//               <span
//                 className={`flex items-center gap-4 xl:text-[1.375rem] text-[1.1rem] font-inter  xl:py-6 py-5 ${
//                   idx === current ? "opacity-100 font-semibold" : "opacity-50"
//                 }`}
//               >
//                 <ReactSVG
//                   src={labelsIcon[idx]}
//                   beforeInjection={(svg) => {
//                     svg.setAttribute("class", "xl:w-6 w-5 h-auto");
//                   }}
//                 />
//                 {barLabels[idx]}
//               </span>
//               {/* Bar */}
//               <div className="w-full bg-[#D9D9D9] rounded-full h-[1px]">
//                 <div
//                   className={`h-[1px] rounded-full transition-all duration-100 ${
//                     idx === current ? "bg-[#3AB1F6]" : "bg-[#D9D9D9]"
//                   }`}
//                   style={{ width: `${value}%` }}
//                 ></div>
//               </div>
//             </div>
//             {idx === current && (
//               <div className="flex md:hidden transition-transform  ease-in-out duration-300 justify-center items-center relative h-max">
//                 {activeData?.type === "json" ? (
//                   <div
//                     className={`w-[85%] rounded-3xl border border-[#3AB1F6] overflow-hidden transition-opacity duration-500 ${
//                       fade ? "opacity-100" : "opacity-0"
//                     }`}
//                   >
//                     <Lottie
//                       animationData={activeData}
//                       loop
//                       style={{ width: "100%" }}
//                     />
//                   </div>
//                 ) : activeData?.type === "video" ? (
//                   <div
//                     className={`w-[85%] rounded-3xl border border-[#3AB1F6] overflow-hidden transition-opacity duration-500 ${
//                       fade ? "opacity-100" : "opacity-0"
//                     }`}
//                   >
//                     <video
//                       key={activeData}
//                       className="scale-105 w-full"
//                       src={activeData?.url}
//                       autoPlay
//                       loop
//                       muted
//                       playsInline
//                       preload="metadata"
//                     />
//                   </div>
//                 ) : null}
//               </div>
//             )}
//           </div>
//         ))}
//       </div>

//       {/* Video Section with fade */}
//       <div className="flex-1 md:flex hidden justify-end items-center relative  xl:h-[70vh] lg:h-[50vh] h-[40vh]">
//         <div
//           ref={videoRef}
//           className={` ${
//             videoView ? `animate-slideLeft` : "opacity-0"
//           } w-[85%] rounded-3xl bg-[#D7EFFC] overflow-hidden transition-opacity duration-500 ${
//             fade ? "opacity-100" : "opacity-0"
//           }`}
//         >
//           {activeData?.type === "json" ? (
//             <Lottie animationData={activeData} loop style={{ width: "100%" }} />
//           ) : activeData?.type === "video" ? (
//             <video
//               key={activeData}
//               className="scale-[1.01] w-full"
//               src={activeData?.url}
//               autoPlay
//               loop
//               muted
//               playsInline
//               preload="metadata"
//             />
//           ) : null}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default HowItWorkVideo;

// import { useEffect, useState } from "react";
// import icon1 from "../../Asset/Install Zero Typing.svg";
// import icon2 from "../../Asset/Complete our guided setup wizard.svg";
// import icon3 from "../../Asset/Open any app with a text field.svg";
// import icon4 from "../../Asset/Place your cursor & Start speaking with Zero Typing app.svg";
// import icon5 from "../../Asset/Watch your words appear instantly.svg";
// import { ReactSVG } from "react-svg";
// import { useInView } from "react-intersection-observer";
// import Lottie from "lottie-react";
// import { useBreakpoint } from "./Home";

// const HowItWorkVideo = () => {
//   const durations = [4, 4, 4, 10, 3];
//   const barLabels = [
//     "Install Zero Typing",
//     "Complete our guided setup wizard",
//     "Open any app with a text field",
//     "Place your cursor & Start speaking with Zero Typing app",
//     "Watch your words appear instantly",
//   ];

//   const { ref: videoRef, inView: videoView } = useInView({
//     triggerOnce: true,
//     threshold: 0.5,
//   });

//   const isMobile = useBreakpoint();
//   const { ref: barRef, inView: barView } = useInView({
//     triggerOnce: true,
//     threshold: isMobile ? 0.1 : 0.5,
//   });

//   const labelsIcon = [icon1, icon2, icon3, icon4, icon5];

//   const howItWorkUrl = [
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270378/INSTALL.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270405/Setup_WIzard.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270366/Open_ANy_App.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1756383603/Start_Recording.mp4",
//     },
//     {
//       type: "video",
//       url: "https://res.cloudinary.com/etresources/video/upload/v1760270381/Text_Appear.mp4",
//     },
//   ];

//   const [howItWorkAnimations, setHowItWorkAnimations] = useState([]);
//   const [progress, setProgress] = useState(Array(durations.length).fill(0));
//   const [current, setCurrent] = useState(0);
//   const [activeData, setActiveData] = useState(null);
//   const [fade, setFade] = useState(true);
//   const [isVideoReady, setIsVideoReady] = useState(false);
//   const [prevVideo, setPrevVideo] = useState(null); // 👈 keep previous video for crossfade

//   const { ref, inView } = useInView({
//     triggerOnce: true,
//     threshold: 0.5,
//   });

//   // initialize animation data
//   useEffect(() => {
//     setHowItWorkAnimations(howItWorkUrl);
//     setActiveData(howItWorkUrl[0]);
//   }, []);

//   // progress control synced with video readiness
//   useEffect(() => {
//     if (!inView || durations.length === 0 || !isVideoReady) return;

//     const duration = durations[current];
//     const step = 100 / (duration * 10);

//     const interval = setInterval(() => {
//       setProgress((prev) => {
//         const updated = [...prev];
//         updated[current] = Math.min(updated[current] + step, 100);
//         return updated;
//       });
//     }, 100);

//     const timeout = setTimeout(() => {
//       clearInterval(interval);
//       setProgress((prev) => {
//         const updated = [...prev];
//         updated[current] = 100;
//         return updated;
//       });

//       // fade out before switching video
//       setFade(false);
//       setTimeout(() => {
//         const nextIndex = (current + 1) % durations.length;
//         const resetProgress = Array(durations.length).fill(0);
//         resetProgress[nextIndex] = 0;
//         setProgress(resetProgress);

//         // save current video to crossfade smoothly
//         setPrevVideo(activeData);
//         setActiveData(howItWorkAnimations[nextIndex]);
//         setCurrent(nextIndex);
//         setIsVideoReady(false);
//         setTimeout(() => setFade(true), 200); // small delay for fade in
//       }, 400);
//     }, duration * 1000);

//     return () => {
//       clearInterval(interval);
//       clearTimeout(timeout);
//     };
//   }, [current, inView, isVideoReady]);

//   const handleClick = (index) => {
//     const resetProgress = Array(durations.length).fill(0);
//     resetProgress[index] = 0;

//     setFade(false);
//     setTimeout(() => {
//       setProgress(resetProgress);
//       setPrevVideo(activeData);
//       setCurrent(index);
//       setActiveData(howItWorkAnimations[index]);
//       setIsVideoReady(false);
//       setFade(true);
//     }, 400);
//   };

//   // reusable smooth video renderer
//   const renderVideo = (data, isDesktop = false) => (
//     <div
//       className={`relative w-full rounded-3xl border ${
//         isDesktop ? "border-transparent bg-[#D7EFFC]" : "border-[#3AB1F6]"
//       } overflow-hidden transition-opacity duration-500 ${
//         fade ? "opacity-100" : "opacity-0"
//       }`}
//     >
//       {/* Previous video fading out */}
//       {prevVideo && (
//         <video
//           key={prevVideo?.url + "_prev"}
//           className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500"
//           src={prevVideo?.url}
//           autoPlay
//           loop
//           muted
//           playsInline
//         />
//       )}

//       {/* Loading state */}
//       {!isVideoReady && (
//         <div className="absolute inset-0 aspect-square w-full flex items-center justify-center transition-opacity duration-300 opacity-100 bg-[#D7EFFC]">
//           <div class="loader"></div>
//         </div>
//       )}

//       {/* New video fading in */}
//       {data?.type === "video" && (
//         <video
//           key={data?.url}
//           className={`w-full h-full object-cover transition-opacity duration-700 ${
//             isVideoReady ? "opacity-100" : "opacity-0"
//           }`}
//           src={data?.url}
//           autoPlay
//           loop
//           muted
//           playsInline
//           preload="auto"
//           onCanPlayThrough={() => setIsVideoReady(true)}
//         />
//       )}
//     </div>
//   );

//   return (
//     <div ref={ref} className="w-full flex items-center z-[5] bg-white mt-5">
//       {/* Left Side: Steps */}
//       <div className="flex-1">
//         {progress.map((value, idx) => (
//           <div
//             ref={barRef}
//             key={idx}
//             className={`flex flex-col gap-4 cursor-pointer ${
//               barView ? `animate-slideRight anim-preserve` : "opacity-0"
//             }`}
//             onClick={() => handleClick(idx)}
//           >
//             <div>
//               <span
//                 className={`flex items-center gap-4 xl:text-[1.375rem] text-[1.1rem] font-inter xl:py-6 py-5 ${
//                   idx === current ? "opacity-100 font-semibold" : "opacity-50"
//                 }`}
//               >
//                 <ReactSVG
//                   src={labelsIcon[idx]}
//                   beforeInjection={(svg) => {
//                     svg.setAttribute("class", "xl:w-6 w-5 h-auto");
//                   }}
//                 />
//                 {barLabels[idx]}
//               </span>

//               {/* Progress Bar */}
//               <div className="w-full bg-[#D9D9D9] rounded-full h-[1px]">
//                 <div
//                   className={`h-[1px] rounded-full transition-all duration-100 ${
//                     idx === current ? "bg-[#3AB1F6]" : "bg-[#D9D9D9]"
//                   }`}
//                   style={{ width: `${value}%` }}
//                 ></div>
//               </div>
//             </div>

//             {/* Mobile View Video */}
//             {idx === current && (
//               <div className="flex md:hidden justify-center items-center relative h-max">
//                 {renderVideo(activeData)}
//               </div>
//             )}
//           </div>
//         ))}
//       </div>

//       {/* Right Side: Desktop Video */}
//       <div className="flex-1 md:flex hidden justify-end items-center relative xl:h-[70vh] lg:h-[50vh] h-[40vh]">
//         <div
//           ref={videoRef}
//           className={`${
//             videoView ? `animate-slideLeft anim-preserve` : "opacity-0"
//           } w-[85%] transition-opacity duration-500`}
//         >
//           {renderVideo(activeData, true)}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default HowItWorkVideo;
